import FullCalendar from '@fullcalendar/react'
import dayGridPlugin   from '@fullcalendar/daygrid'
import timeGridPlugin  from '@fullcalendar/timegrid'
import listPlugin      from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import { Link, router } from '@inertiajs/react'
import InfoTip from '../../components/InfoTip'
import { useMemo, useRef, useState } from 'react'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function timeUntil(iso) {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'Now'
  const totalMin = Math.round(ms / 60_000)
  if (totalMin < 60) return `${totalMin}m`
  const hours = Math.floor(totalMin / 60)
  const mins  = totalMin % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

const STATUS_STYLE = {
  pending:    { label: 'Scheduled', bg: 'bg-blue-100',   text: 'text-blue-700'   },
  activating: { label: 'Starting',  bg: 'bg-yellow-100', text: 'text-yellow-700' },
  active:     { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700'  },
  cancelled:  { label: 'Cancelled', bg: 'bg-red-100',    text: 'text-red-700'    },
  completed:  { label: 'Complete',  bg: 'bg-gray-100',   text: 'text-gray-600'   },
}

// Activation-mode → calendar event color
const MODE_COLOR = {
  immediate: '#2563EB',  // blue-600
  pending:   '#D97706',  // amber-600
  scheduled: '#7C3AED',  // violet-600
}

// Status → calendar event color (overrides activation_mode when terminal)
const STATUS_COLOR = {
  active:    '#16A34A',  // green-600
  cancelled: '#DC2626',  // red-600
  completed: '#9CA3AF',  // gray-400
}

function eventColor(s) {
  return STATUS_COLOR[s.status] ?? MODE_COLOR[s.activation_mode] ?? '#2563EB'
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-600' }
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  )
}

function ModeBadge({ mode }) {
  const styles = {
    immediate: 'bg-blue-100 text-blue-700',
    pending:   'bg-amber-100 text-amber-700',
    scheduled: 'bg-violet-100 text-violet-700',
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${styles[mode] ?? 'bg-gray-100 text-gray-600'}`}>
      {mode ?? 'immediate'}
    </span>
  )
}

// ─── Schedule table ───────────────────────────────────────────────────────────

function ScheduleTable({ propertySessions }) {
  if (propertySessions.length === 0) {
    return <p className="text-sm text-gray-400 italic py-4">No scheduled sessions match the current filter.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Guest</th>
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Offer</th>
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Mode</th>
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduled</th>
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Until</th>
            <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
            <th className="pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Queue</th>
          </tr>
        </thead>
        <tbody>
          {propertySessions.flatMap(ps =>
            ps.scheduled_sessions.map(s => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 pr-4">
                  <p className="font-medium text-gray-800">{ps.guest_name}</p>
                  <p className="text-xs text-gray-400">PS #{ps.id}</p>
                </td>
                <td className="py-2 pr-4">
                  <p className="text-gray-800">{s.offer_name}</p>
                  {s.position_hint && (
                    <p className="text-xs text-gray-400">pos. ~{s.position_hint}</p>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <ModeBadge mode={s.activation_mode} />
                </td>
                <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                  {formatAt(s.scheduled_at)}
                  {s.expires_at && (
                    <p className="text-xs text-gray-400">exp. {formatAt(s.expires_at)}</p>
                  )}
                </td>
                <td className="py-2 pr-4 text-blue-600 font-medium whitespace-nowrap">
                  {timeUntil(s.scheduled_at)}
                </td>
                <td className="py-2 pr-4">
                  <StatusBadge status={s.status} />
                </td>
                <td className="py-2">
                  {s.queue_session_id ? (
                    <Link
                      href={`/admin/queue_monitor/${s.queue_session_id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      #{s.queue_session_id}
                    </Link>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

// ─── Calendar event tooltip ───────────────────────────────────────────────────

function EventTooltip({ event, position, onClose }) {
  if (!event) return null
  const { guestName, offerName, activationMode, status, queueSessionId, positionHint } = event.extendedProps
  return (
    <div
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-72 text-sm"
      style={{ top: position.y, left: position.x }}
    >
      <div className="flex justify-between items-start mb-2">
        <p className="font-semibold text-gray-900 leading-tight">{offerName}</p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2 shrink-0">✕</button>
      </div>
      <p className="text-gray-600 mb-2">{guestName}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        <ModeBadge mode={activationMode} />
        <StatusBadge status={status} />
      </div>
      <p className="text-xs text-gray-500 mb-1">
        {formatAt(event.startStr)} → {event.endStr ? formatAt(event.endStr) : '—'}
      </p>
      {positionHint && <p className="text-xs text-gray-400 mb-3">Position hint: ~{positionHint}</p>}
      {queueSessionId ? (
        <Link
          href={`/admin/queue_monitor/${queueSessionId}`}
          className="block text-center text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={onClose}
        >
          View Queue #{queueSessionId}
        </Link>
      ) : (
        <p className="text-xs text-gray-400 italic text-center">Queue not yet created</p>
      )}
    </div>
  )
}

// ─── Calendar view ────────────────────────────────────────────────────────────

function ScheduleCalendar({ propertySessions }) {
  const calendarRef = useRef(null)
  const [tooltip, setTooltip] = useState({ event: null, position: { x: 0, y: 0 } })

  // Flatten property_sessions → FullCalendar event objects
  const events = useMemo(() => {
    return propertySessions.flatMap(ps =>
      ps.scheduled_sessions.map(s => {
        const start = s.scheduled_at
        // Use expires_at as end; fall back to 1-hour block so event has visible height
        const end = s.expires_at ?? new Date(new Date(s.scheduled_at).getTime() + 60 * 60_000).toISOString()
        const color = eventColor(s)
        return {
          id:              String(s.id),
          title:           `${ps.guest_name} — ${s.offer_name}`,
          start,
          end,
          backgroundColor: color,
          borderColor:     color,
          textColor:       '#fff',
          extendedProps: {
            guestName:      ps.guest_name,
            offerName:      s.offer_name,
            activationMode: s.activation_mode,
            status:         s.status,
            queueSessionId: s.queue_session_id,
            positionHint:   s.position_hint,
          },
        }
      })
    )
  }, [propertySessions])

  function handleEventClick({ event, jsEvent }) {
    jsEvent.preventDefault()
    const rect = jsEvent.target.getBoundingClientRect()
    const x = Math.min(rect.left, window.innerWidth - 300)
    const y = rect.bottom + 8
    setTooltip({ event, position: { x, y } })
  }

  return (
    <>
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        {[
          { label: 'Immediate', color: '#2563EB' },
          { label: 'Pending',   color: '#D97706' },
          { label: 'Scheduled', color: '#7C3AED' },
          { label: 'Active',    color: '#16A34A' },
          { label: 'Cancelled', color: '#DC2626' },
          { label: 'Completed', color: '#9CA3AF' },
        ].map(({ label, color }) => (
          <span key={label} className="flex items-center gap-1.5 text-gray-600">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left:   'prev,next today',
          center: 'title',
          right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
        }}
        buttonText={{
          today:    'Today',
          month:    'Month',
          week:     'Week',
          day:      'Day',
          list:     'Agenda',
        }}
        events={events}
        eventClick={handleEventClick}
        eventDisplay="block"
        height="auto"
        slotMinTime="06:00:00"
        slotMaxTime="23:00:00"
        nowIndicator
        allDaySlot={false}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: 'short' }}
      />

      {tooltip.event && (
        <>
          {/* Click-away backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setTooltip({ event: null, position: { x: 0, y: 0 } })} />
          <EventTooltip
            event={tooltip.event}
            position={tooltip.position}
            onClose={() => setTooltip({ event: null, position: { x: 0, y: 0 } })}
          />
        </>
      )}
    </>
  )
}

// ─── Bulk Schedule Modal ──────────────────────────────────────────────────────

function BulkScheduleModal({ onClose }) {
  const [rows, setRows]       = useState([{ property_session_id: '', offer_id: '', scheduled_at: '', expires_at: '' }])
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult]   = useState(null)

  function addRow() {
    setRows(r => [...r, { property_session_id: '', offer_id: '', scheduled_at: '', expires_at: '' }])
  }

  function updateRow(idx, field, value) {
    setRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row))
  }

  function removeRow(idx) {
    setRows(r => r.filter((_, i) => i !== idx))
  }

  function submit() {
    setSubmitting(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch('/admin/schedule_monitor/bulk_schedule', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify({ schedules: rows }),
    })
      .then(r => r.json())
      .then(data => {
        setResult(data)
        setSubmitting(false)
        if (data.errors?.length === 0) {
          router.reload({ only: ['property_sessions'] })
        }
      })
      .catch(() => setSubmitting(false))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-screen overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">Bulk Schedule Sessions</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-4 space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-4 gap-2 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Property Session ID</label>
                <input
                  type="number"
                  value={row.property_session_id}
                  onChange={e => updateRow(idx, 'property_session_id', e.target.value)}
                  placeholder="e.g. 42"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Offer ID</label>
                <input
                  type="number"
                  value={row.offer_id}
                  onChange={e => updateRow(idx, 'offer_id', e.target.value)}
                  placeholder="e.g. 36"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Scheduled At</label>
                <input
                  type="datetime-local"
                  value={row.scheduled_at}
                  onChange={e => updateRow(idx, 'scheduled_at', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex gap-1 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Expires At (opt.)</label>
                  <input
                    type="datetime-local"
                    value={row.expires_at}
                    onChange={e => updateRow(idx, 'expires_at', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {rows.length > 1 && (
                  <button
                    onClick={() => removeRow(idx)}
                    className="text-red-400 hover:text-red-600 text-sm pb-1"
                  >✕</button>
                )}
              </div>
            </div>
          ))}
        </div>

        {result && (
          <div className={`mx-4 p-3 rounded text-sm ${result.errors?.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {result.created?.length > 0 && <p>✓ Created {result.created.length} session(s).</p>}
            {result.errors?.map((e, i) => (
              <p key={i}>✗ {e.error} — offer {e.entry?.offer_id}</p>
            ))}
          </div>
        )}

        <div className="p-4 border-t border-gray-200 flex justify-between">
          <button onClick={addRow} className="text-sm text-blue-600 hover:underline">+ Add row</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Scheduling…' : 'Schedule All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const VIEW_TABLE    = 'table'
const VIEW_CALENDAR = 'calendar'

export default function ScheduleMonitor({ property_sessions, filters }) {
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [view, setView] = useState(VIEW_CALENDAR)

  function applyFilter(key, value) {
    router.get('/admin/schedule_monitor', { ...filters, [key]: value }, { replace: true })
  }

  const totalScheduled = property_sessions.reduce((sum, ps) => sum + ps.scheduled_sessions.length, 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Schedule Monitor</h1>
            <p className="text-sm text-gray-500 mt-1">
              {totalScheduled} scheduled session{totalScheduled !== 1 ? 's' : ''} across{' '}
              {property_sessions.length} active stay{property_sessions.length !== 1 ? 's' : ''}.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button
                onClick={() => setView(VIEW_TABLE)}
                className={`px-3 py-1.5 transition-colors ${
                  view === VIEW_TABLE
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setView(VIEW_CALENDAR)}
                className={`px-3 py-1.5 border-l border-gray-300 transition-colors ${
                  view === VIEW_CALENDAR
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Calendar
              </button>
            </div>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
            >
              + Bulk Schedule
            </button>
          </div>
        </div>

        {/* Filters — shown in both views */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status:</span>
            {['upcoming', 'pending', 'active', 'cancelled', 'all'].map(s => (
              <button
                key={s}
                onClick={() => applyFilter('status', s)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  (filters.status || 'upcoming') === s
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Main content — table or calendar */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          {view === VIEW_TABLE
            ? <ScheduleTable propertySessions={property_sessions} />
            : <ScheduleCalendar propertySessions={property_sessions} />
          }
        </div>

        {/* Nav */}
        <div className="mt-4 flex gap-4 text-sm text-gray-500">
          <Link href="/admin/queue_monitor"   className="text-blue-600 hover:underline">← Queue Monitor</Link>
          <Link href="/admin/network_monitor" className="text-blue-600 hover:underline">Network Monitor</Link>
          <Link href="/admin/journey_monitor" className="text-blue-600 hover:underline">Journey Monitor</Link>
        </div>
      </div>

      {showBulkModal && <BulkScheduleModal onClose={() => setShowBulkModal(false)} />}
    </div>
  )
}
